/* eslint-disable react-refresh/only-export-components -- utility functions co-located with component for cohesion */
/**
 * Injects an SVG <defs> hatching pattern into Leaflet's overlay SVG.
 *
 * The pattern has two layers:
 *   1. <rect class="choropleth-hatch-bg"/>   — background, repainted on each
 *      hover to match the hovered feature's choropleth colour.
 *   2. <rect>                                 — translucent white diagonal
 *      stripe drawn over the background.
 *
 * We can't use `currentColor` here because SVG paint servers (patterns,
 * gradients) resolve `currentColor` in their own context, not in the
 * referencing element's. So instead we expose a stable selector
 * (`.choropleth-hatch-bg`) and ChoroplethLayer updates its `fill` attribute
 * inline on mouseover.
 */

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

export const HATCH_PATTERN_ID = 'choropleth-hatch';
export const HATCH_LOCKED_PATTERN_ID = 'choropleth-hatch-locked';
export const HATCH_BG_CLASS = 'choropleth-hatch-bg';
export const HATCH_LOCKED_BG_CLASS = 'choropleth-hatch-locked-bg';

// Hover и locked делят одинаковую визуальную модель: прозрачный фон
// + цветные диагональные полоски. Подложка карты (улицы, названия) чётко
// видна между полосками. Отличие только в bg-цвете полосок:
// - hover: красится временно через setHatchColor (preview наведения)
// - locked: красится через setLockedHatchColor (фиксируется при клике)
// Два паттерна нужны чтобы hover не перебивал цвет залоченного региона.
function makeStripePattern(id: string, stripeClass: string): SVGPatternElement {
  const ns = 'http://www.w3.org/2000/svg';
  const pattern = document.createElementNS(ns, 'pattern');
  pattern.setAttribute('id', id);
  pattern.setAttribute('patternUnits', 'userSpaceOnUse');
  pattern.setAttribute('width', '8');
  pattern.setAttribute('height', '8');
  pattern.setAttribute('patternTransform', 'rotate(45)');

  const bg = document.createElementNS(ns, 'rect');
  bg.setAttribute('width', '8');
  bg.setAttribute('height', '8');
  bg.setAttribute('fill', 'transparent');

  const stripe = document.createElementNS(ns, 'rect');
  stripe.setAttribute('class', stripeClass);
  stripe.setAttribute('width', '4');
  stripe.setAttribute('height', '8');
  stripe.setAttribute('fill', '#666');

  pattern.appendChild(bg);
  pattern.appendChild(stripe);
  return pattern;
}

function makeHoverPattern(): SVGPatternElement {
  return makeStripePattern(HATCH_PATTERN_ID, HATCH_BG_CLASS);
}

function makeLockedPattern(): SVGPatternElement {
  return makeStripePattern(HATCH_LOCKED_PATTERN_ID, HATCH_LOCKED_BG_CLASS);
}

function buildDefs(): SVGDefsElement {
  const ns = 'http://www.w3.org/2000/svg';
  const defs = document.createElementNS(ns, 'defs');
  defs.appendChild(makeHoverPattern());
  defs.appendChild(makeLockedPattern());
  return defs;
}

/** Repaint the hover hatch pattern's stripes (transparent bg, coloured stripes). */
export function setHatchColor(color: string): void {
  const stripe = document.querySelector(
    `#${HATCH_PATTERN_ID} .${HATCH_BG_CLASS}`,
  );
  if (stripe) stripe.setAttribute('fill', color);
}

/** Repaint the locked (clicked) hatch pattern's stripes. */
export function setLockedHatchColor(color: string): void {
  const stripe = document.querySelector(
    `#${HATCH_LOCKED_PATTERN_ID} .${HATCH_LOCKED_BG_CLASS}`,
  );
  if (stripe) stripe.setAttribute('fill', color);
}

export function HatchPatternDefs() {
  const map = useMap();

  useEffect(() => {
    const inject = () => {
      const pane = map.getPanes().overlayPane;
      const svg = pane?.querySelector('svg');
      if (!svg) return;
      if (svg.querySelector(`#${HATCH_PATTERN_ID}`)) return;
      svg.insertBefore(buildDefs(), svg.firstChild);
    };

    inject();
    map.on('layeradd', inject);
    return () => {
      map.off('layeradd', inject);
    };
  }, [map]);

  return null;
}
